import { createId } from '../core/ids';
import { defaultFilterValues, type FilterId } from '../filters';
import type { DocumentMeta, Layer, SmartFilter, SmartLayer } from '../core/types';
import type { Engine } from './engine';
import { filterPass } from './filterPass';
import type { EngineHistoryOp } from './historyOps';

/** Copies the current surface into the smart original and returns a smart layer. */
export function toSmartLayer(layer: Layer): SmartLayer {
  const rest = { ...layer } as Layer & { smartFilters?: SmartFilter[]; kind: string };
  delete rest.smartFilters;
  return {
    ...(rest as unknown as Omit<SmartLayer, 'kind' | 'smartFilters'>),
    kind: 'smart',
    smartFilters: [],
  };
}

export function toRasterFromSmart(layer: SmartLayer): Layer {
  const rest = { ...layer };
  delete (rest as { smartFilters?: SmartFilter[] }).smartFilters;
  return { ...(rest as Omit<SmartLayer, 'kind' | 'smartFilters'>), kind: 'raster' };
}

/**
 * Seeds the smart-object original from the live surface. Call after the meta
 * kind flip so subsequent composites replay filters from this copy.
 */
export function captureSmartSource(engine: Engine, doc: DocumentMeta, layerId: string): void {
  if (!engine.available) return;
  const resources = engine.resourcesFor(doc);
  const layer = resources.ensureLayer(layerId);
  const source = resources.ensureSmartSource(layerId);
  engine.renderer.copy(source.framebuffer, layer.surface.texture);
}

/** Bakes enabled smart filters into the surface and drops the original. */
export function bakeSmartLayer(
  engine: Engine,
  doc: DocumentMeta,
  layer: SmartLayer,
): void {
  if (!engine.available) return;
  const resources = engine.resourcesFor(doc);
  const original = resources.ensureSmartSource(layer.id);
  let current = original.texture;
  const temps: ReturnType<typeof resources.buffers.color.acquire>[] = [];
  for (const entry of layer.smartFilters) {
    if (!entry.enabled) continue;
    const pass = filterPass(engine, resources, entry.filter, entry.values, current);
    const target = resources.buffers.color.acquire();
    engine.renderer.applyFilter(target.framebuffer, current, null, pass);
    temps.push(target);
    current = target.texture;
  }
  const surface = resources.ensureLayer(layer.id).surface;
  engine.renderer.copy(surface.target.framebuffer, current);
  for (const temp of temps) resources.buffers.color.release(temp);
  resources.clearSmartSource(layer.id);
}

export function makeSmartFilter(filter: FilterId, values?: Record<string, number>): SmartFilter {
  return {
    id: createId('sfilter'),
    filter,
    values: values ?? defaultFilterValues(filter),
    enabled: true,
  };
}

/** No pixel history — smart filter lists live in document meta. */
export function smartFilterMetaOnly(_ops: EngineHistoryOp[] | null): null {
  return null;
}
